import { useCallback, useEffect, useRef } from "react";
import { findDropTarget } from "./helpers";
import { DropInfo, DropTarget } from "../GalleryItemsLayout";
import { Item } from "@thenounproject/lingo-core";
import { throttle } from "lodash";

const EDGE_THRESHOLD = 100;
const THROTTLE_AMT = 32;
// const GAP_THRESHOLD = 30;
// const ABOVE_BELOW_THRESHOLD = 0.15;

interface UseDragDropParams {
  container: HTMLElement | null;
  onDrop?: (e: React.DragEvent, dropInfo: DropInfo) => void;
  items: Item[];
}

export function useCanvasDragDrop({ container, onDrop, items }: UseDragDropParams) {
  const currentDropInfoRef = useRef<{ index: number; position: "before" | "after" } | null>(null);
  const currentIndicatorRef = useRef<HTMLElement | null>(null);
  const isOverContainerRef = useRef(false);
  const droppableItemsCacheRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (container) {
      droppableItemsCacheRef.current = Array.from(
        container.querySelectorAll("[data-droppable-item]")
      );
    }
  }, [container, items]);

  const updateDragState = useCallback(
    (isDragging: boolean) => {
      if (container) {
        container.setAttribute("data-is-dragging", isDragging.toString());
      }
    },
    [container]
  );

  const clearDropIndicators = useCallback(() => {
    if (!container) return;

    container.querySelectorAll('[data-active="true"]').forEach(el => {
      el.setAttribute("data-active", "false");
    });

    container.querySelectorAll(".drop-indicator-active").forEach(el => {
      el.remove();
    });

    if (currentIndicatorRef.current) {
      currentIndicatorRef.current.remove();
      currentIndicatorRef.current = null;
    }
  }, [container]);

  const createDropIndicator = useCallback(
    (dropTarget: DropTarget) => {
      if (!container) return;

      if (currentIndicatorRef.current) {
        currentIndicatorRef.current.remove();
        currentIndicatorRef.current = null;
      }

      const itemElement = dropTarget.element.hasAttribute("data-droppable-item")
        ? dropTarget.element
        : (dropTarget.element.querySelector("[data-droppable-item]") as HTMLElement);

      if (!itemElement) return;
      const contentElement = itemElement.firstElementChild as HTMLElement;
      const itemRect = itemElement.getBoundingClientRect();
      const contentRect = contentElement?.getBoundingClientRect() ?? itemRect;
      const containerRect = container.getBoundingClientRect();
      const isFullWidth = itemElement.getAttribute("data-full-width") === "true";

      const indicator = document.createElement("div");
      indicator.className = "drop-indicator-between drop-indicator-active";
      indicator.setAttribute("data-visible", "true");

      const applyIndicatorStyles = (styles: Partial<CSSStyleDeclaration>) => {
        Object.assign(indicator.style, {
          position: "absolute",
          background: "#3b82f6",
          borderRadius: "2px",
          zIndex: "20",
          pointerEvents: "none",
          ...styles,
        });
      };

      const setHorizontalIndicator = (top: number, width?: number, left?: number) => {
        applyIndicatorStyles({
          top: `${top}px`,
          left: `${left ?? itemRect.left - containerRect.left}px`,
          width: `${width ?? itemRect.width}px`,
          height: "4px",
        });
      };

      const setVerticalIndicator = (left: number) => {
        applyIndicatorStyles({
          top: `${contentRect.top - containerRect.top}px`,
          left: `${left}px`,
          height: `${contentRect.height}px`,
          width: "4px",
        });
      };

      if (isFullWidth) {
        const cursorX = dropTarget.cursorX ?? itemRect.left + itemRect.width / 2;
        const cursorY = dropTarget.cursorY ?? itemRect.top + itemRect.height / 2;
        const isVerticallyWithinItem = cursorY >= itemRect.top && cursorY <= itemRect.bottom;
        const isNearLeftEdge = cursorX < itemRect.left + EDGE_THRESHOLD;
        const isNearRightEdge = cursorX > itemRect.right - EDGE_THRESHOLD;

        if (isVerticallyWithinItem && isNearLeftEdge) {
          setVerticalIndicator(itemRect.left - containerRect.left - 2);
        } else if (isVerticallyWithinItem && isNearRightEdge) {
          setVerticalIndicator(itemRect.right - containerRect.left - 2);
        } else {
          const HALF_GAP = 16;
          const top =
            dropTarget.position === "before"
              ? itemRect.top - containerRect.top - 2 - HALF_GAP
              : itemRect.bottom - containerRect.top - 2 + HALF_GAP;
          setHorizontalIndicator(top);
        }
      } else {
        const HALF_GAP = 8;
        const left =
          dropTarget.position === "before"
            ? itemRect.left - containerRect.left - 2 - HALF_GAP
            : itemRect.right - containerRect.left - 2 + HALF_GAP;
        setVerticalIndicator(left);
      }

      container.appendChild(indicator);
      currentIndicatorRef.current = indicator;
    },
    [container]
  );

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      if (e.currentTarget === e.target || container?.contains(e.target as Node)) {
        isOverContainerRef.current = true;
        updateDragState(true);
      }
    },
    [container, updateDragState]
  );

  const handleDragLeave = useCallback(
    (e: DragEvent) => {
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const { clientX, clientY } = e;

      const isInsideBounds =
        clientX >= containerRect.left &&
        clientX <= containerRect.right &&
        clientY >= containerRect.top &&
        clientY <= containerRect.bottom;

      if (!isInsideBounds) {
        isOverContainerRef.current = false;
        updateDragState(false);
        clearDropIndicators();
        currentDropInfoRef.current = null;
      }
    },
    [container, updateDragState, clearDropIndicators]
  );

  const throttledFindDropTarget = useRef(
    throttle((e: DragEvent, callback: (target: DropTarget | null) => void) => {
      const target = findDropTarget(e, droppableItemsCacheRef.current);
      callback(target);
    }, THROTTLE_AMT)
  ).current;

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = e.altKey ? "copy" : "move";

      if (!container || !isOverContainerRef.current) return;

      throttledFindDropTarget(e, dropTarget => {
        if (!isOverContainerRef.current) {
          clearDropIndicators();
          return;
        }

        if (!dropTarget) {
          clearDropIndicators();
          currentDropInfoRef.current = null;
          return;
        }

        const newDropInfo = {
          index: dropTarget.index,
          position: dropTarget.position,
        };

        if (
          !currentDropInfoRef.current ||
          currentDropInfoRef.current.index !== newDropInfo.index ||
          currentDropInfoRef.current.position !== newDropInfo.position
        ) {
          clearDropIndicators();
          createDropIndicator(dropTarget);
          currentDropInfoRef.current = newDropInfo;
        }
      });
    },
    [container, clearDropIndicators, createDropIndicator, throttledFindDropTarget]
  );

  const buildDropInfo = useCallback(
    (dropTarget: DropTarget): DropInfo | null => {
      const itemId = dropTarget.element.getAttribute("data-item-id") || "";
      const itemIndex = dropTarget.index;
      const targetItem = items[itemIndex];
      const insertIndex = dropTarget.position === "before" ? itemIndex : itemIndex + 1;
      const dragPosition: DropInfo["dragPosition"] =
        dropTarget.position === "before" ? "left" : "right";

      return {
        type: "between-items",
        insertIndex,
        displayOrder: dropTarget.position === "before" ? `before:${itemId}` : `after:${itemId}`,
        dragPosition,
        targetItem,
        position: dropTarget.position,
      };
    },
    [items]
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!container) return;

      const cleanup = () => {
        clearDropIndicators();
        updateDragState(false);
        currentDropInfoRef.current = null;
      };

      if (!onDrop) {
        cleanup();
        return;
      }

      const dropTarget = findDropTarget(e);
      if (!dropTarget) {
        cleanup();
        return;
      }

      const dropInfo = buildDropInfo(dropTarget);

      if (dropInfo) {
        onDrop(e as unknown as React.DragEvent<Element>, dropInfo);
      }

      cleanup();
    },
    [container, onDrop, clearDropIndicators, updateDragState, buildDropInfo]
  );

  return {
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    updateDragState,
  };
}
